import { moveToFulfilled, moveToPending, moveToRejected, remove } from '@ms/yammer-data-support';
import { TopicSubscriptionNotificationTarget } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import {
  TopicsAllLoadFulfilledAction,
  TopicsAllLoadMoreFulfilledAction,
} from '../../domains/engageTopicsAll/actions.types';
import {
  SuggestedTopicsLoadFulfilledAction,
  SuggestedTopicsLoadMoreFulfilledAction,
} from '../../domains/suggestedTopics/actions.types';
import { FollowTopicAction, UnfollowTopicAction } from '../../domains/topicFollow/actions.types';
import { TopicHeaderLoadAction } from '../../domains/topicHeaderLoad/actions.types';
import { SubscribeTopicAction, UnsubscribeTopicAction } from '../../domains/topicSubscription/actions.types';
import { CreateCuratedTopicFulfilledAction } from '../../domains/topics/createCurateActions.types';
import { TopicCurateAction } from '../../domains/topics/curateActions.types';
import { TopicEditAction } from '../../domains/topics/editActions.types';
import { RemoveCuratedTopicFulfilledAction } from '../../domains/topics/removeCuratedActions.types';
import { RemoveTopicFulfilledProps } from '../../domains/topics/removeTopicActions';
import { RemoveTopicFulfilledAction } from '../../domains/topics/removeTopicActions.types';
import {
  TopicsCuratedLoadFulfilledAction,
  TopicsCuratedLoadMoreFulfilledAction,
} from '../../domains/topicsCurated/actions.types';
import { TopicsFollowedLoadAction, TopicsFollowedLoadMoreAction } from '../../domains/topicsFollowedLoad/actions.types';
import {
  TrendingTopicsLoadFulfilledAction,
  TrendingTopicsLoadMoreFulfilledAction,
} from '../../domains/trendingTopicsLoad/actions.types';
import { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';
import { Topic } from '../types';

import { TopicNotificationSubscription, TopicNotificationSubscriptions } from './types';

export type TopicNotificationSubscriptionAction =
  | FollowTopicAction
  | TopicHeaderLoadAction
  | UnfollowTopicAction
  | TopicCurateAction
  | CreateCuratedTopicFulfilledAction
  | TopicEditAction
  | TrendingTopicsLoadFulfilledAction
  | TrendingTopicsLoadMoreFulfilledAction
  | TopicsAllLoadFulfilledAction
  | TopicsAllLoadMoreFulfilledAction
  | TopicsCuratedLoadFulfilledAction
  | TopicsCuratedLoadMoreFulfilledAction
  | SuggestedTopicsLoadFulfilledAction
  | SuggestedTopicsLoadMoreFulfilledAction
  | RemoveCuratedTopicFulfilledAction
  | TopicsFollowedLoadAction
  | TopicsFollowedLoadMoreAction
  | TypeaheadFulfilledAction
  | RemoveTopicFulfilledAction
  | SubscribeTopicAction
  | UnsubscribeTopicAction;

const initialState: TopicNotificationSubscriptions = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const subscribeTopics = (state: TopicNotificationSubscriptions, topicIds: Topic['id'][]) => {
  const entities: Record<Topic['id'], TopicNotificationSubscription> = {};
  const viewerNotificationSubscriptions = [
    TopicSubscriptionNotificationTarget.TEAMS,
    TopicSubscriptionNotificationTarget.IN_APP,
  ];

  topicIds.forEach((topicId) => {
    // eslint-disable-next-line functional/immutable-data
    entities[topicId] = {
      id: topicId,
      viewerNotificationSubscriptions,
    };
  });

  return moveToFulfilled(state, entities);
};

const removeTopic = (state: TopicNotificationSubscriptions, topicIds: RemoveTopicFulfilledProps) => {
  const curatedTopicId = topicIds?.curatedTopicId;

  return curatedTopicId ? remove(state, [curatedTopicId]) : state;
};

const reducer: Reducer<TopicNotificationSubscriptions, TopicNotificationSubscriptionAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case 'TOPIC_HEADER.LOAD_PENDING':
      return moveToPending(state, [action.payload.topicId]);
    case 'TOPIC_HEADER.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.topicId], action.payload.error);
    case 'TOPIC_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TRENDING_TOPICS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TRENDING_TOPICS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TOPICS_FOLLOWED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TOPIC.CURATE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TOPIC.CREATE_AND_CURATE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TOPICS_ALL.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TOPICS_ALL.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TOPICS_CURATED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TOPICS_CURATED.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'SUGGESTED_TOPICS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'SUGGESTED_TOPICS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicNotificationSubscriptions);
    case 'CURATED_TOPIC.REMOVE_FULFILLED':
      return remove(state, [action.payload.topicId]);
    case 'TOPIC.REMOVE_FULFILLED':
      return removeTopic(state, action.payload);
    case 'TOPIC.SUBSCRIBE_FULFILLED':
      return subscribeTopics(state, action.payload.topicIds);
    case 'TOPIC.UNSUBSCRIBE_FULFILLED':
      return remove(state, action.payload.topicIds);
    default:
      return state;
  }
};

export default reducer;
